/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useLocation, matchPath } from 'react-router';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  makeStyles,
  IconButton,
  Grid,
} from '@material-ui/core';
import {
  BarChart as ReportsIcon,
  PieChart as DashboardIcon,
  Settings as ConfigurationIcon,
  ChevronsLeft as CloseNavBarIcon,
  ChevronsRight as OpenNavBarIcon,
  ShoppingCart as RewardIcon,
  Globe as SitesIcon,
  Heart as ClubIcon,
  Tag as PointAllocationIcon,
} from 'react-feather';
import NavItem from './NavItem';

const navConfig = (navOpen) => [
  {
    subheader: navOpen ? 'Dasboard' : 'DB',
    key: 'Dasboard',
    items: [
      {
        title: 'Dashboard',
        icon: DashboardIcon,
        href: '/app/dashboard'
      },
      {
        title: 'Reports',
        icon: ReportsIcon,
        href: '/app/reports'
      },
    ]
  },
  {
    subheader: navOpen ? 'Configuration' : 'Config',
    key: 'Configuration',
    items: [
      {
        title: 'Details',
        icon: ConfigurationIcon,
        href: '/app/configuration/details'
      },
      {
        title: 'Sites',
        icon: SitesIcon,
        href: '/app/configuration/sites'
      },
      {
        title: 'Clubs',
        icon: ClubIcon,
        href: '/app/configuration/clubs'
      },
      {
        title: 'Rewards',
        icon: RewardIcon,
        href: '/app/configuration/rewards'
      },
      {
        title: 'Point Allocation',
        icon: PointAllocationIcon,
        href: '/app/configuration/pointAllocation'
      },
    ]
  },
];

const reduceChildRoutes = ({
  acc,
  pathname,
  item,
  depth = 0,
  navOpen
}) => {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        open={Boolean(open)}
        title={(navOpen ? item.title : '')}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          navOpen
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={(navOpen ? item.title : '')}
      />
    );
  }

  return acc;
};

const renderNavItems = ({ items, navOpen, ...rest }) => (
  <List disablePadding>
    {items.reduce(
      (acc, item) => reduceChildRoutes({
        acc, item, navOpen, ...rest
      }),
      []
    )}
  </List>
);

const drawerWidth = 256;

const useStyles = makeStyles((theme) => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  drawerOpen: {
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: theme.spacing(7) + 1,
    [theme.breakpoints.up('sm')]: {
      width: theme.spacing(9) + 1,
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
  },
}));

const NavBar = ({ openMobile, onMobileClose, }) => {
  const classes = useStyles();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(localStorage.getItem('navOpen') !== 'false');

  useEffect(() => {
    const checkNavOpen = () => {
      const item = localStorage.getItem('navOpen');

      if (item) {
        setNavOpen(item === 'true');
      }
    };

    window.addEventListener('storage', checkNavOpen);

    return () => {
      window.removeEventListener('storage', checkNavOpen);
    };
  }, []);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box
      style={{ marginTop: (openMobile ? 0 : 50) }}
      height="100%"
      display="flex"
      flexDirection="column"
    >
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box
            p={1}
            display="flex"
            justifyContent="center"
          />
        </Hidden>

        <Box p={2}>
          {navConfig(navOpen || openMobile).map((config) => (
            <List
              key={config.key}
              subheader={(
                <ListSubheader
                  disableGutters
                  disableSticky
                >
                  {config.subheader}
                </ListSubheader>
              )}
            >
              {renderNavItems({
                items: config.items,
                pathname: location.pathname,
                navOpen: (navOpen || openMobile)
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>

      {openMobile ? null
        : (
          <div>
            {navOpen
              ? (
                <Grid
                  container
                  direction="row"
                  justify="flex-end"
                  alignItems="center"
                >
                  <IconButton
                    size="small"
                    style={{ marginTop: -50, marginRight: 10 }}
                    onClick={() => {
                      localStorage.setItem('navOpen', !navOpen);
                      setNavOpen(!navOpen);
                      window.dispatchEvent(new Event('storage'));
                    }}
                  >
                    <CloseNavBarIcon />
                  </IconButton>
                </Grid>
              )
              : (
                <Grid
                  container
                  direction="row"
                  justify="center"
                  alignItems="center"
                >
                  <IconButton
                    size="small"
                    style={{ marginTop: -50 }}
                    onClick={() => {
                      localStorage.setItem('navOpen', !navOpen);
                      setNavOpen(!navOpen);
                      window.dispatchEvent(new Event('storage'));
                    }}
                  >
                    <OpenNavBarIcon />
                  </IconButton>
                </Grid>
              )}
          </div>
        )}

    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          className={clsx(classes.drawer, {
            [classes.drawerOpen]: navOpen,
            [classes.drawerClose]: !navOpen,
          })}
          classes={{
            paper: clsx({
              [classes.drawerOpen]: navOpen,
              [classes.drawerClose]: !navOpen,
            }),
          }}
          variant="permanent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
};

export default NavBar;
